import PropTypes from "prop-types";

import { actionPrefixes, objectTypes, actionNames } from "@newsela/snitch";
import SelectedStateIndicator from "@/components/SelectedStateIndicator";
import ActionsDropdown from "@/view/ActionsDropdown";
import { pollsProptype } from "@/view/PollContainer/PollProptypes";
import usePollContext from "@/view/utils/usePollContext";
import {
  buttonCss,
  popOutContentsCss,
  popOutButtonItemCss,
  popOutContentsRootCss,
} from "./PollsDropdown.style";

const propTypes = {
  activePollId: PropTypes.string,
  currentContentLevelId: PropTypes.string,
  polls: pollsProptype,
};

const PollsDropdown = ({ activePollId, currentContentLevelId, polls }) => {
  const { assignedPoll, setActivePoll, setIsCreateMode } = usePollContext();

  const getEventProperties = (description) => {
    return {
      objectType: objectTypes.poll,
      actionPrefix: actionPrefixes.initiate,
      actionName: actionNames.navigate,
      streamProperties: {
        content_type: "Article",
        content_id: currentContentLevelId,
        object_id: activePollId,
        description,
      },
    };
  };

  const dropdownItemEventProperties = (pollId) =>
    getEventProperties(`Select poll template ${pollId}`);
  const dropdownEventProperties = getEventProperties("Open poll dropdown");
  const assignedPollEventProperties = getEventProperties(
    `Select assigned poll ${assignedPoll?.id}`,
  );

  const handlePollSelection = (closeDropdown, poll) => {
    setActivePoll(poll);
    closeDropdown();
  };

  const assignedPollDropdownItem = (closeDropdown) => {
    const isSelected = assignedPoll?.id === activePollId;

    return (
      <ActionsDropdown.ButtonItem
        key={assignedPoll?.id}
        __cssFor={{ Button: { root: popOutButtonItemCss } }}
        ariaProps={{ "aria-current": isSelected }}
        dataProps={{ "data-qa-selector": "poll-selector-item-assigned-poll" }}
        eventProperties={assignedPollEventProperties}
        isSelected={isSelected}
        onClick={() => handlePollSelection(closeDropdown, assignedPoll)}
      >
        <SelectedStateIndicator shouldDisplay={isSelected} />
        {assignedPoll?.data?.question}
      </ActionsDropdown.ButtonItem>
    );
  };

  const newPollDropdownItem = () => {
    const newPollEventProperties = {
      objectType: objectTypes.poll,
      actionName: actionNames.create,
      actionPrefix: actionPrefixes.initiate,
      streamProperties: {
        content_type: "Article",
        content_id: currentContentLevelId,
        description: "Create new poll",
      },
    };

    const isSelected = false;

    return (
      <ActionsDropdown.ButtonItem
        key="poll-selector-item-new-poll"
        __cssFor={{ Button: { root: popOutButtonItemCss } }}
        ariaProps={{ "aria-current": isSelected }}
        dataProps={{ "data-qa-selector": "poll-selector-item-new-poll" }}
        eventProperties={newPollEventProperties}
        isSelected={isSelected}
        onClick={() => setIsCreateMode(true)}
      >
        <SelectedStateIndicator shouldDisplay={isSelected} />+ Start from
        scratch
      </ActionsDropdown.ButtonItem>
    );
  };

  const renderDropdownItems = (closeDropdown) => {
    const pollOptions = polls?.map((poll) => {
      const isSelected = poll.id === activePollId;

      return (
        <ActionsDropdown.ButtonItem
          key={poll.id}
          __cssFor={{ Button: { root: popOutButtonItemCss } }}
          ariaProps={{ "aria-current": isSelected }}
          dataProps={{ "data-qa-selector": `poll-selector-item-${poll.id}` }}
          eventProperties={dropdownItemEventProperties(poll.id)}
          isSelected={isSelected}
          onClick={() => handlePollSelection(closeDropdown, poll)}
        >
          <SelectedStateIndicator shouldDisplay={isSelected} />
          {poll.data.question}
        </ActionsDropdown.ButtonItem>
      );
    });

    if (assignedPoll) {
      return [assignedPollDropdownItem(closeDropdown), ...pollOptions];
    }

    return [newPollDropdownItem(), pollOptions];
  };

  return (
    <ActionsDropdown
      ariaProps={{ "aria-label": "Choose another poll" }}
      buttonCss={buttonCss}
      buttonText="Choose another poll"
      dataProps={{ "data-qa-selector": "more-polls-selector" }}
      eventProperties={dropdownEventProperties}
      id="more-polls-selector"
      popOutCss={{
        popOutContents: popOutContentsCss,
        root: popOutContentsRootCss,
      }}
      renderDropdownItems={renderDropdownItems}
    />
  );
};

PollsDropdown.propTypes = propTypes;

export default PollsDropdown;
